import { type AuthEnv, Environment, environmentToAuthEnvMap } from '@/types';

const getRunTimeConfig = () => {
  if (typeof (<any>window).bxSrtSupportAppParams === 'object') {
    return (<any>window).bxSrtSupportAppParams || {};
  }

  return {};
};

const runTimeConfig = getRunTimeConfig();

/**
 * Resolves the Dex audience for the given deployment environment. The result is appended to
 * the OIDC scope as `audience:server:client_id:<authEnv>` and ends up as the `aud` claim of
 * the issued access token.
 *
 * Returns `undefined` when the environment cannot be resolved (e.g. `VITE_ENVIRONMENT` unset);
 * the OIDC client then refuses to start (see `buildScope` in `src/services/oidcClient.ts`).
 */
export function getAuthEnv(env?: string): AuthEnv | undefined {
  return environmentToAuthEnvMap[env as Environment];
}

const resolvedEnvironment = runTimeConfig.environment ?? import.meta.env.VITE_ENVIRONMENT;

export const environment = Object.freeze({
  appBaseUrl: import.meta.env.VITE_APP_BASE_URL,
  dittoApiUrl: import.meta.env.VITE_DITTO_API_URL,
  zendeskBaseUrl: new URLSearchParams(window.location.search).get('origin'),
  dexOidcAuthority: import.meta.env.VITE_DEX_OIDC_AUTHORITY,
  dexOidcClientId: import.meta.env.VITE_DEX_OIDC_CLIENT_ID,
  srtSerialCustomFieldId: import.meta.env.VITE_SRT_SERIAL_CUSTOM_FIELD_ID,
  allAffectedSrtSerialsCustomFieldId: import.meta.env.VITE_ALL_AFFECTED_SRT_SERIALS_CUSTOM_FIELD_ID,
  customerNumberLookupCustomFieldId: import.meta.env.VITE_CUSTOMER_NUMBER_LOOKUP_CUSTOM_FIELD_ID,
  billingUnitNumberLookupCustomFieldId: import.meta.env
    .VITE_BILLING_UNIT_NUMBER_LOOKUP_CUSTOM_FIELD_ID,
  billingUnitNumberCustomObjectKey: import.meta.env.VITE_BILLING_UNIT_NUMBER_CUSTOM_OBJECT_KEY,
  addressCustomFieldId: import.meta.env.VITE_ADDRESS_CUSTOM_FIELD_ID,
  usageUnitCustomFieldId: import.meta.env.VITE_USAGE_UNIT_CUSTOM_FIELD_ID,
  dateOfInstallationCustomFieldId: import.meta.env.VITE_DATE_OF_INSTALLATION_CUSTOM_FIELD_ID,
  floorLevelCustomFieldId: import.meta.env.VITE_FLOOR_LEVEL_CUSTOM_FIELD_ID,
  roomTypeCustomFieldId: import.meta.env.VITE_ROOM_TYPE_CUSTOM_FIELD_ID,
  firmwareVersionCustomFieldId: import.meta.env.VITE_FIRMWARE_VERSION_CUSTOM_FIELD_ID,
  articleNumberCustomFieldId: import.meta.env.VITE_ARTICLE_NUMBER_CUSTOM_FIELD_ID,
  residentCustomFieldId: import.meta.env.VITE_RESIDENT_CUSTOM_FIELD_ID,
  environment: resolvedEnvironment,
  sentryDSN: import.meta.env.VITE_SENTRY_DSN,
  askAuthApiUrl: import.meta.env.VITE_ASK_AUTH_API_URL,
  askApiBasicAuthPlaceholder: import.meta.env.VITE_ASK_API_BASIC_AUTH_PLACEHOLDER_NAME,
  // OAuth2 client_id of the ASK API client, sent in the token request
  // body. The client_secret is held by the Zendesk secure setting named above.
  // Must be set per environment (see helm values / params.js); an empty value
  // will produce an invalid token request. See services/askApi.ts.
  askApiClientId: import.meta.env.VITE_ASK_API_CLIENT_ID,
  askApiUrl: import.meta.env.VITE_ASK_API_URL,
  cpApiUrl: import.meta.env.VITE_CP_API_URL,

  /**
   * Allow config specified at run time to override everything above.
   */
  ...runTimeConfig,

  /** Derived from `environment`, so it has to stay below the run time config spread. */
  authEnv: getAuthEnv(resolvedEnvironment),
});

declare interface BxSrtSupportAppWindow extends Window {
  appBaseUrl: string;
  dittoApiUrl: string;
  zendeskBaseUrl: string;
  dexOidcAuthority: string;
  dexOidcClientId: string;
  srtSerialCustomFieldId: string;
  allAffectedSrtSerialsCustomFieldId: string;
  customerNumberLookupCustomFieldId: string;
  billingUnitNumberLookupCustomFieldId: string;
  billingUnitNumberCustomObjectKey: string;
  addressCustomFieldId: string;
  usageUnitCustomFieldId: string;
  dateOfInstallationCustomFieldId: string;
  floorLevelCustomFieldId: string;
  roomTypeCustomFieldId: string;
  firmwareVersionCustomFieldId: string;
  articleNumberCustomFieldId: string;
  residentCustomFieldId: string;
  environment: Environment;
  sentryDSN: string;
  askAuthApiUrl: string;
  askApiBasicAuthPlaceholder: string;
  askApiClientId: string;
  askApiUrl: string;
  cpApiUrl: string;
}

declare const window: BxSrtSupportAppWindow | typeof globalThis;
